import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useQuery } from '@apollo/client';
import { faHandshake } from '@fortawesome/free-solid-svg-icons';
import { GET_REFERRAL_COMMISSIONS } from '../../services/referralCommissionService/queries';
import {
  Alert,
  Badge,
  BadgeTone,
  Card,
  CardBody,
  EmptyState,
  LoadingBlock,
  PageContainer,
  PageHeader,
  Stack,
  StatCard,
  StatGrid,
  Table,
  TableWrap,
  Tabs,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../../components/ui';

const Primary = styled.div`
  ${tw`text-sm font-medium`}
  color: var(--color-text-primary);
`;

const Secondary = styled.div`
  ${tw`text-xs mt-0.5`}
  color: var(--color-text-secondary);
`;

// ---- Helpers ---------------------------------------------------------------

function formatUSD(amount?: number | null): string {
  if (amount == null) return '$0.00';
  return `$${Number(amount).toFixed(2)}`;
}

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  return new Date(Number(iso)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function commissionTone(status: string): BadgeTone {
  if (status === 'EARNED') return 'success';
  if (status === 'VOIDED') return 'error';
  return 'warning';
}

type TabKey = 'asReferrer' | 'asCarOwner';

// ---- Component -------------------------------------------------------------

export default function ReferralCommissionsPage() {
  const [activeTab, setActiveTab] = useState<TabKey>('asReferrer');

  const { data, loading, error } = useQuery(GET_REFERRAL_COMMISSIONS, {
    fetchPolicy: 'network-only',
  });

  const commissions: any[] = data?.referralCommissions?.commissions ?? [];
  const asReferrer = data?.referralCommissions?.asReferrer;
  const asCarOwner = data?.referralCommissions?.asCarOwner;

  const brandSite: string | null = data?.referralCommissions?.brandSite ?? null;
  // D21: owed to this brand = rows referred from here; owed by this brand = rows checked out here.
  const filtered = commissions.filter(c =>
    brandSite
      ? activeTab === 'asReferrer'
        ? c.referringBrandSite === brandSite
        : c.brandSite === brandSite
      : true,
  );

  const summary = activeTab === 'asReferrer' ? asReferrer : asCarOwner;

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title={
          <>
            Referral commissions{' '}
            {brandSite && (
              <Badge tone="brand" size="md">
                {brandSite}
              </Badge>
            )}
          </>
        }
        description={`Fees between brands. "Owed to us" lists bookings that other brands took after a redirect from ${
          brandSite ?? 'this brand'
        }; "Owed by us" lists bookings guests were redirected to ${
          brandSite ?? 'this brand'
        } from another brand. The percent is the checkout brand's rate on the car price, locked when the booking was confirmed.`}
      />

      <Stack>
        <Tabs
          aria-label="Direction"
          items={[
            { key: 'asReferrer', label: 'Owed to us' },
            { key: 'asCarOwner', label: 'Owed by us' },
          ]}
          value={activeTab}
          onChange={setActiveTab}
        />

        {summary && (
          <StatGrid $cols={3}>
            <StatCard label="Pending" value={formatUSD(summary.pendingAmount)} />
            <StatCard label="Earned" value={formatUSD(summary.earnedAmount)} />
            <StatCard label="Voided" value={formatUSD(summary.voidedAmount)} />
          </StatGrid>
        )}

        {loading && <LoadingBlock label="Loading commissions" />}

        {error && (
          <Alert tone="error" title="Failed to load commissions">
            Please try again.
          </Alert>
        )}

        {!loading && !error && (
          <Card>
            {filtered.length === 0 ? (
              <EmptyState
                icon={faHandshake}
                title="No commissions yet"
                description="Cross-brand bookings will show up here once a guest books after a redirect."
              />
            ) : (
              <CardBody $padding="none">
                <TableWrap>
                  <Table>
                    <THead>
                      <tr>
                        <TH>Booking</TH>
                        <TH>Between</TH>
                        <TH>Car owner</TH>
                        <TH $align="right">Rate</TH>
                        <TH $align="right">Basis</TH>
                        <TH $align="right">Commission</TH>
                        <TH>Status</TH>
                      </tr>
                    </THead>
                    <TBody>
                      {filtered.map((commission: any) => {
                        const car = commission.booking?.car;
                        const carLabel = car
                          ? `${car.year} ${car.make} ${car.model}`
                          : `Booking ${commission.bookingId?.slice(0, 8)}`;
                        const tripLabel =
                          commission.booking?.startAt && commission.booking?.endAt
                            ? `${formatDate(commission.booking.startAt)} – ${formatDate(commission.booking.endAt)}`
                            : '—';
                        const owner = commission.carOwnerHost;
                        const ownerLabel = owner
                          ? `${owner.firstName ?? ''} ${owner.lastName ?? ''}`.trim() ||
                            owner.displayName ||
                            owner.id
                          : '—';

                        return (
                          <TR key={commission.id}>
                            <TD>
                              <Primary>{carLabel}</Primary>
                              <Secondary>{tripLabel}</Secondary>
                            </TD>
                            <TD>
                              <Primary>{commission.brandSite}</Primary>
                              <Secondary>owes {commission.referringBrandSite}</Secondary>
                            </TD>
                            <TD>{ownerLabel}</TD>
                            <TD $align="right" $nowrap>
                              {commission.commissionRate ?? 0}%
                            </TD>
                            <TD $align="right" $nowrap $muted>
                              {formatUSD(commission.commissionBasis)}
                            </TD>
                            <TD $align="right" $nowrap>
                              <Primary>{formatUSD(commission.commissionAmount)}</Primary>
                              {commission.earnedAt && (
                                <Secondary>Earned {formatDate(commission.earnedAt)}</Secondary>
                              )}
                            </TD>
                            <TD>
                              <Badge tone={commissionTone(commission.status)} dot>
                                {commission.status}
                              </Badge>
                            </TD>
                          </TR>
                        );
                      })}
                    </TBody>
                  </Table>
                </TableWrap>
              </CardBody>
            )}
          </Card>
        )}
      </Stack>
    </PageContainer>
  );
}
